import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { isDemoUser, simulateDemoAction } from "@/lib/demoMode";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/helpers";
import { Plus, CreditCard, Receipt, DollarSign } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "office_staff";
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [payForm, setPayForm] = useState({ student_id: "", amount: "", method: "cash", memo: "" });
  const [chargeForm, setChargeForm] = useState({ student_id: "", description: "", amount: "" });

  const load = useCallback(async () => {
    try {
      const promises = [api.get("/payments")];
      if (isStaff) promises.push(api.get("/students"));
      const results = await Promise.all(promises);
      setPayments(results[0].data);
      if (isStaff) setStudents(results[1].data);
    } catch {
      toast.error("Could not load payments");
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => {
    load();
  }, [load]);

  const submitPayment = async () => {
    if (!payForm.student_id || !payForm.amount) {
      toast.error("Select a student and enter an amount");
      return;
    }
    if (isDemoUser(user)) {
      const student = students.find((s) => s.id === payForm.student_id);
      const preview = {
        id: `demo-${Date.now()}`, student_name: student?.full_name || "Student",
        amount: parseFloat(payForm.amount), method: payForm.method, status: "paid",
        created_at: new Date().toISOString(), receipt_pdf_url: null,
      };
      setPayments((prev) => [preview, ...prev]);
      simulateDemoAction(`a $${parseFloat(payForm.amount).toFixed(2)} payment would be recorded instantly and a receipt emailed to the family.`);
      setPayOpen(false);
      setPayForm({ student_id: "", amount: "", method: "cash", memo: "" });
      return;
    }
    try {
      await api.post("/payments", { ...payForm, amount: parseFloat(payForm.amount) });
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ student_id: "", amount: "", method: "cash", memo: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    }
  };

  const submitCharge = async () => {
    if (!chargeForm.student_id || !chargeForm.description || !chargeForm.amount) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await api.post("/charges", { ...chargeForm, amount: parseFloat(chargeForm.amount) });
      toast.success("Charge added");
      setChargeOpen(false);
      setChargeForm({ student_id: "", description: "", amount: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add charge");
    }
  };

  const refund = async (id) => {
    try {
      await api.post(`/payments/${id}/refund`, { reason: "Refunded by admin" });
      toast.success("Payment refunded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not refund");
    }
  };

  if (loading) return <Skeleton className="h-64 rounded-[var(--radius)]" />;

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {isStaff ? "Record payments, issue refunds, and track outstanding balances." : "View your payment history and download receipts."}
        </p>
        {isStaff && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setChargeOpen(true)} data-testid="add-charge-button">
              <DollarSign className="h-4 w-4 mr-2" /> Add Charge
            </Button>
            <Button onClick={() => setPayOpen(true)} className="bg-[hsl(var(--role-accent))] hover:opacity-95" data-testid="record-payment-button">
              <Plus className="h-4 w-4 mr-2" /> Record Payment
            </Button>
          </div>
        )}
      </div>

      {payments.length === 0 ? (
        <div className="rounded-[var(--radius)] border bg-card p-12 text-center space-y-2" data-testid="payments-empty-state">
          <CreditCard className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
        </div>
      ) : (
        <div className="rounded-[var(--radius)] border bg-card overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead className="hidden sm:table-cell">Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} data-testid="payment-row">
                  <TableCell>{formatDate(p.created_at)}</TableCell>
                  <TableCell className="font-medium">{p.student_name}</TableCell>
                  <TableCell className="tabular-nums">${p.amount.toFixed(2)}</TableCell>
                  <TableCell className="hidden sm:table-cell capitalize">{p.method?.replace("_", " ")}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="flex items-center gap-2">
                    {p.receipt_pdf_url && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${p.receipt_pdf_url}`} target="_blank" rel="noreferrer" className="text-[hsl(var(--role-accent))]" data-testid="download-receipt-link">
                        <Receipt className="h-4 w-4" />
                      </a>
                    )}
                    {isStaff && p.status === "paid" && (
                      <Button size="sm" variant="ghost" onClick={() => refund(p.id)} data-testid="refund-payment-button">
                        Refund
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle>Record a Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Student</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={payForm.student_id} onChange={(e) => setPayForm((f) => ({ ...f, student_id: e.target.value }))} data-testid="payment-form-student-select">
                <option value="">Select student</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.full_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} data-testid="payment-form-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Method</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={payForm.method} onChange={(e) => setPayForm((f) => ({ ...f, method: e.target.value }))} data-testid="payment-form-method-select">
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="card_manual">Card (manual)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Memo (optional)</Label>
              <Textarea value={payForm.memo} onChange={(e) => setPayForm((f) => ({ ...f, memo: e.target.value }))} data-testid="payment-form-memo-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitPayment} data-testid="payment-form-submit-button">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={chargeOpen} onOpenChange={setChargeOpen}>
        <DialogContent data-testid="add-charge-dialog">
          <DialogHeader>
            <DialogTitle>Add Charge</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Student</Label>
              <select className="w-full h-11 rounded-[var(--radius)] border bg-background px-3 text-sm" value={chargeForm.student_id} onChange={(e) => setChargeForm((f) => ({ ...f, student_id: e.target.value }))} data-testid="charge-form-student-select">
                <option value="">Select student</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.full_name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input placeholder="e.g. 10-hour lesson package" value={chargeForm.description} onChange={(e) => setChargeForm((f) => ({ ...f, description: e.target.value }))} data-testid="charge-form-description-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Amount ($)</Label>
              <Input type="number" step="0.01" value={chargeForm.amount} onChange={(e) => setChargeForm((f) => ({ ...f, amount: e.target.value }))} data-testid="charge-form-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submitCharge} data-testid="charge-form-submit-button">Add Charge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
